import { useState, useEffect, useMemo, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Camera, ChevronDown, Loader2, Plus, Trash2, ChevronLeft, ChevronRight, X } from "lucide-react";
import { StorageThumbnailImage } from "@/components/media/StorageThumbnailImage";
import { getAttachments, createSignedAttachmentUrl, uploadAttachment, deleteAttachment } from "@/services/attachment.service";
import { JobService } from "@/services/job.service";
import { toast } from "sonner";
import type { Job, Issue, Attachment, JobPhotoGroup } from "@/data/types";
import { isRenderablePhoto, collectJobPhotoUrls } from "@/lib/photoUtils";
import { getStorageStableKey } from "@/lib/storageImageUrls";

function JobPhotoGallery({ job, linkedQuoteId, allIssues }: { job: Job; linkedQuoteId: string | null; allIssues: Issue[] }) {
  const [quotePhotoUrls, setQuotePhotoUrls] = useState<string[]>([]);
  const [groupedPhotos, setGroupedPhotos] = useState<Record<string, { id: string; url: string }[]>>({});
  const [photosLoading, setPhotosLoading] = useState(false);
  const [uploadingCounts, setUploadingCounts] = useState<Record<string, number>>({});
  const [lightbox, setLightbox] = useState<{ photos: string[]; index: number } | null>(null);
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set(["general"]));
  const [localPhotoGroups, setLocalPhotoGroups] = useState<JobPhotoGroup[]>(job.photoGroups ?? []);
  const [newGroupLabel, setNewGroupLabel] = useState("");
  const [savingGroup, setSavingGroup] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!linkedQuoteId) {
        if (!cancelled) setQuotePhotoUrls([]);
        return;
      }
      try {
        const records = await getAttachments("quote", linkedQuoteId);
        const photoRecords = records.filter((r: Attachment) =>
          r.attachmentType === "photo" && String(r.notes ?? "").startsWith("site_photo:"),
        );
        const urls = await Promise.all(
          photoRecords.map(async (r) => {
            const path = String(r.filePath ?? "").trim();
            if (!path) return "";
            return createSignedAttachmentUrl(path, "job-documents").catch(() => "");
          }),
        );
        if (!cancelled) {
          const seen = new Set<string>();
          const deduped = urls.filter((u) => {
            const v = String(u || "").trim();
            if (!isRenderablePhoto(v) || seen.has(v)) return false;
            seen.add(v);
            return true;
          });
          setQuotePhotoUrls(deduped);
        }
      } catch {
        if (!cancelled) setQuotePhotoUrls([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [linkedQuoteId]);

  const loadGroupedPhotos = useCallback(async () => {
    setPhotosLoading(true);
    try {
      const records = await getAttachments("job", job.id);
      const photoRecords = records.filter(
        (r: Attachment) => r.attachmentType === "photo" && String(r.notes ?? "").startsWith("job_photo"),
      );
      const bucket: Record<string, { id: string; url: string }[]> = {};
      await Promise.all(
        photoRecords.map(async (r: Attachment) => {
          const notes = String(r.notes ?? "");
          const groupId = notes === "job_photo" ? "general" : notes.slice("job_photo:".length);
          const path = String(r.filePath ?? "").trim();
          if (!path) return;
          const url = await createSignedAttachmentUrl(path, "job-photos").catch(() => "");
          if (!url || !isRenderablePhoto(url)) return;
          bucket[groupId] = [...(bucket[groupId] ?? []), { id: r.id, url }];
        }),
      );
      setGroupedPhotos(bucket);
    } catch {
      setGroupedPhotos({});
    } finally {
      setPhotosLoading(false);
    }
  }, [job.id]);

  useEffect(() => {
    void loadGroupedPhotos();
  }, [loadGroupedPhotos]);

  const handleGroupPhotoUpload = useCallback(
    async (groupId: string, e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? []);
      if (files.length === 0) return;
      e.target.value = "";
      const notes = groupId === "general" ? "job_photo" : `job_photo:${groupId}`;
      setUploadingCounts((prev) => ({ ...prev, [groupId]: (prev[groupId] ?? 0) + files.length }));
      await Promise.all(
        files.map(async (file) => {
          try {
            const attachment = await uploadAttachment(file, "job", job.id, "photo", "Koredor", notes);
            const path = String(attachment.filePath ?? "").trim();
            const url = path
              ? await createSignedAttachmentUrl(path, "job-photos").catch(() => "")
              : "";
            if (url && isRenderablePhoto(url)) {
              setGroupedPhotos((prev) => ({
                ...prev,
                [groupId]: [...(prev[groupId] ?? []), { id: attachment.id, url }],
              }));
            }
          } catch {
            toast.error("Failed to upload photo. Please try again.");
          } finally {
            setUploadingCounts((prev) => ({
              ...prev,
              [groupId]: Math.max(0, (prev[groupId] ?? 1) - 1),
            }));
          }
        }),
      );
    },
    [job.id],
  );

  const handleGroupPhotoDelete = useCallback(
    async (groupId: string, id: string) => {
      setGroupedPhotos((prev) => ({
        ...prev,
        [groupId]: (prev[groupId] ?? []).filter((p) => p.id !== id),
      }));
      try {
        await deleteAttachment(id);
      } catch {
        toast.error("Failed to delete photo.");
        void loadGroupedPhotos();
      }
    },
    [loadGroupedPhotos],
  );

  const handleAddGroup = useCallback(async () => {
    const label = newGroupLabel.trim();
    if (!label) return;
    setSavingGroup(true);
    try {
      const newGroup: JobPhotoGroup = {
        id: crypto.randomUUID(),
        label,
        photoRefs: [],
        createdAt: new Date().toISOString(),
      };
      const updatedGroups = [...(job.photoGroups ?? []), newGroup];
      await JobService.update(job.id, { photoGroups: updatedGroups });
      setLocalPhotoGroups(updatedGroups);
      setNewGroupLabel("");
      setOpenGroups((prev) => new Set([...prev, newGroup.id]));
    } catch {
      toast.error("Failed to create group.");
    } finally {
      setSavingGroup(false);
    }
  }, [newGroupLabel, job.id, job.photoGroups]);

  const rawJobPhotoUrls = useMemo(() => collectJobPhotoUrls(job, allIssues), [job, allIssues]);

  const allPhotoUrls = useMemo(() => {
    const seen = new Set<string>();
    return [...rawJobPhotoUrls, ...quotePhotoUrls].filter((u) => {
      const key = getStorageStableKey(u, u);
      if (!isRenderablePhoto(u) || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [rawJobPhotoUrls, quotePhotoUrls]);

  return (
    <div className="space-y-5">
      <Card className="shadow-none">
        <CardContent className="p-4">
          <div className="flex items-center justify-between pb-1.5 mb-3 border-b">
            <div className="flex items-center gap-2">
              <Camera className="h-4 w-4 text-muted-foreground" />
              <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em]">Photos</h2>
            </div>
          </div>

          {quotePhotoUrls.length > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-4">
              {quotePhotoUrls.map((url, i) => (
                <button key={`quote-${i}`} type="button" onClick={() => setLightbox({ photos: quotePhotoUrls, index: i })} className="block aspect-square overflow-hidden rounded border bg-muted/20 hover:opacity-90 transition-opacity cursor-zoom-in">
                  <StorageThumbnailImage
                    src={url}
                    bucket="job-documents"
                    alt={`Site photo ${i + 1}`}
                    className="h-full w-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}

          <div className="flex gap-2 mb-3">
            <Input
              value={newGroupLabel}
              onChange={(e) => setNewGroupLabel(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") void handleAddGroup(); }}
              placeholder="e.g. Guest Bathroom"
              className="h-7 text-xs"
            />
            <button
              type="button"
              onClick={() => void handleAddGroup()}
              disabled={!newGroupLabel.trim() || savingGroup}
              className="shrink-0 flex items-center gap-1 rounded px-2 py-1 text-[11px] font-medium bg-muted hover:bg-muted/80 disabled:opacity-40 transition-colors"
            >
              <Plus className="h-3 w-3" />
              Add Group
            </button>
          </div>

          <div className="space-y-1.5">
            {([{ id: "general", label: "General" }, ...localPhotoGroups] as { id: string; label: string }[]).map((group) => {
              const photos = groupedPhotos[group.id] ?? [];
              const pendingCount = uploadingCounts[group.id] ?? 0;
              const isOpen = openGroups.has(group.id);
              return (
                <div key={group.id} className="border rounded-md overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setOpenGroups((prev) => {
                      const next = new Set(prev);
                      if (next.has(group.id)) next.delete(group.id); else next.add(group.id);
                      return next;
                    })}
                    className="w-full flex items-center justify-between px-3 py-2 text-xs font-medium hover:bg-muted/30 transition-colors"
                  >
                    <span>{group.label}</span>
                    <div className="flex items-center gap-2">
                      {(photos.length + pendingCount) > 0 && (
                        <span className="text-[10px] text-muted-foreground tabular-nums">{photos.length + pendingCount}</span>
                      )}
                      <ChevronDown className={`h-3 w-3 text-muted-foreground transition-transform duration-150 ${isOpen ? "rotate-180" : ""}`} />
                    </div>
                  </button>
                  {isOpen && (
                    <div className="px-3 pb-3 pt-2 border-t">
                      <label className="inline-flex items-center gap-1 cursor-pointer rounded px-2 py-1 text-[11px] font-medium text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors mb-2">
                        <Camera className="h-3 w-3" />
                        Add Photos
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          capture="environment"
                          className="sr-only"
                          onChange={(e) => void handleGroupPhotoUpload(group.id, e)}
                        />
                      </label>
                      {photos.length === 0 && pendingCount === 0 ? (
                        <p className="text-[11px] text-muted-foreground italic">No photos in this group yet.</p>
                      ) : (
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                          {photos.map((photo, i) => (
                            <div key={photo.id} className="relative group aspect-square overflow-hidden rounded border bg-muted/20">
                              <button type="button" onClick={() => setLightbox({ photos: photos.map(p => p.url), index: i })} className="block h-full w-full hover:opacity-90 transition-opacity cursor-zoom-in">
                                <StorageThumbnailImage
                                  src={photo.url}
                                  bucket="job-photos"
                                  alt={`${group.label} photo ${i + 1}`}
                                  className="h-full w-full object-cover"
                                />
                              </button>
                              <button
                                type="button"
                                onClick={() => void handleGroupPhotoDelete(group.id, photo.id)}
                                className="absolute top-1 right-1 hidden group-hover:flex items-center justify-center h-5 w-5 rounded-full bg-black/60 hover:bg-destructive/80 transition-colors"
                                aria-label="Delete photo"
                              >
                                <Trash2 className="h-3 w-3 text-white" />
                              </button>
                            </div>
                          ))}
                          {Array.from({ length: pendingCount }).map((_, i) => (
                            <div key={`uploading-${group.id}-${i}`} className="aspect-square rounded border bg-muted/20 flex items-center justify-center">
                              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {photosLoading && (
            <div className="flex items-center gap-2 mt-3 text-[11px] text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />
              Loading photos…
            </div>
          )}
        </CardContent>
      </Card>

      {lightbox && (() => {
        const THUMBS_PER_PAGE = 6;
        const thumbPage = Math.floor(lightbox.index / THUMBS_PER_PAGE);
        const thumbStart = thumbPage * THUMBS_PER_PAGE;
        const thumbEnd = Math.min(thumbStart + THUMBS_PER_PAGE, lightbox.photos.length);
        const totalThumbPages = Math.ceil(lightbox.photos.length / THUMBS_PER_PAGE);
        return (
          <div
            className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
            onClick={() => setLightbox(null)}
            onKeyDown={(e) => {
              if (e.key === "Escape") setLightbox(null);
              if (e.key === "ArrowLeft") setLightbox(prev => prev && prev.index > 0 ? { ...prev, index: prev.index - 1 } : prev);
              if (e.key === "ArrowRight") setLightbox(prev => prev && prev.index < prev.photos.length - 1 ? { ...prev, index: prev.index + 1 } : prev);
            }}
            tabIndex={-1}
            ref={(el) => el?.focus()}
          >
            <div
              className="bg-[#111] rounded-xl flex flex-col w-full max-w-4xl max-h-[85vh] overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-4 py-3 bg-black/60 shrink-0">
                <span className="text-white text-sm font-medium">All job photos</span>
                <div className="flex items-center gap-4">
                  <span className="text-white/60 text-sm tabular-nums">{lightbox.index + 1} / {lightbox.photos.length}</span>
                  <button type="button" onClick={() => setLightbox(null)} className="text-white/70 hover:text-white" aria-label="Close">
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <div className="flex-1 relative flex items-center justify-center overflow-hidden">
                <img
                  src={lightbox.photos[lightbox.index]}
                  alt={`Photo ${lightbox.index + 1}`}
                  className="max-h-full max-w-full object-contain"
                />
                {lightbox.index > 0 && (
                  <button
                    type="button"
                    onClick={() => setLightbox(prev => prev ? { ...prev, index: prev.index - 1 } : prev)}
                    className="absolute left-3 p-2 rounded-full bg-black/50 hover:bg-black/70 text-white"
                    aria-label="Previous"
                  >
                    <ChevronLeft className="h-6 w-6" />
                  </button>
                )}
                {lightbox.index < lightbox.photos.length - 1 && (
                  <button
                    type="button"
                    onClick={() => setLightbox(prev => prev ? { ...prev, index: prev.index + 1 } : prev)}
                    className="absolute right-3 p-2 rounded-full bg-black/50 hover:bg-black/70 text-white"
                    aria-label="Next"
                  >
                    <ChevronRight className="h-6 w-6" />
                  </button>
                )}
              </div>

              {lightbox.photos.length > 1 && (
                <div className="shrink-0 bg-black/80 px-4 pt-2 pb-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white/50 text-xs">Showing {thumbStart + 1}–{thumbEnd} of {lightbox.photos.length}</span>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        disabled={thumbPage === 0}
                        onClick={() => setLightbox(prev => prev ? { ...prev, index: (thumbPage - 1) * THUMBS_PER_PAGE } : prev)}
                        className="text-xs text-white/60 hover:text-white disabled:opacity-30 px-2 py-1 rounded border border-white/20"
                      >
                        Prev thumbs
                      </button>
                      <button
                        type="button"
                        disabled={thumbPage >= totalThumbPages - 1}
                        onClick={() => setLightbox(prev => prev ? { ...prev, index: (thumbPage + 1) * THUMBS_PER_PAGE } : prev)}
                        className="text-xs text-white/60 hover:text-white disabled:opacity-30 px-2 py-1 rounded border border-white/20"
                      >
                        Next thumbs
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-6 gap-2">
                    {lightbox.photos.slice(thumbStart, thumbEnd).map((url, j) => {
                      const i = thumbStart + j;
                      return (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setLightbox(prev => prev ? { ...prev, index: i } : prev)}
                          className={`relative aspect-square rounded overflow-hidden border-2 transition-colors ${i === lightbox.index ? "border-white" : "border-transparent opacity-60 hover:opacity-90"}`}
                        >
                          <img src={url} alt="" className="h-full w-full object-cover" />
                          <span className="absolute bottom-0.5 left-0 right-0 text-center text-[10px] text-white/80 tabular-nums">{i + 1}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

export { JobPhotoGallery };
